import React, { HTMLAttributes, useCallback } from 'react';
import cn from 'classnames';

import RequestSubmittedIcon from '../../assets/img/submitted-icon.svg';
import RequestFailedIcon from '../../assets/img/failed-icon.svg';
import PATH from '../../constants/paths';
import { useHistory } from 'react-router-dom';
import AwardPaymentLayout from './AwardPaymentLayout';
import AwardPaymentPageHeader from './AwardPaymentPageHeader';
import AwardPaymentPageContainer from './AwardPaymentPageContainer';
import { AWARD_PAYMENT } from '../../constants/general';

interface ContainerProps extends HTMLAttributes<HTMLDivElement> {}

const STEPS = [
  'Your Successful Futures Specialist will review your request within 3-5 business days.',
  'You will receive an email notification when the status of your request changes.',
  'You can track the status of your request anytime on the Award Payment Requests page.',
  "If additional information is needed, you'll be able to ask questions or upload documents directly from the request page.",
];

const styles = {
  contentBox: 'bg-white rounded-xl border border-solid border-brand-cyan p-[60px] flex flex-col items-start self-stretch',
  iconContainer: 'flex justify-center self-stretch',
  icon: 'w-20 h-20',
  titleSection: 'pt-6 flex flex-col items-center self-stretch gap-0',
  title: 'text-blue text-center text-4xl leading-[60px] font-extrabold',
  descriptionSection: 'pt-4 flex flex-col items-center self-stretch',
  description: 'text-brand-navy text-center text-lg leading-[27px]',
  stepsSection: 'pt-8 flex flex-col self-stretch',
  stepsBox: 'bg-gray-100 rounded-lg p-6',
  stepsTitle: 'text-brand-navy text-left text-xl leading-[30px] font-bold',
  stepsList: 'pt-4 space-y-3',
  stepItem: 'flex gap-3',
  stepNumber: 'text-brand-navy font-bold min-w-fit',
  stepText: 'text-gray-800 text-base leading-6',
  buttonContainer: 'pt-8 flex gap-4 self-stretch items-center justify-center',
  viewRequestsButton: 'bg-blue hover:bg-blue-700 text-white font-bold text-sm border-none uppercase rounded-full py-3 px-8 transition-colors',
  returnHomeButton: 'bg-gray-300 hover:bg-gray-400 text-brand-navy font-bold text-sm border-none uppercase rounded-full py-3 px-8 transition-colors',
};

const pageTitle = 'Request Response';

const RequestResponse: React.FC<ContainerProps> = ({ className = '', ...props }) => {
  const responseStatusParam = new URLSearchParams(window.location.search).get('status');
  const responseStatus = responseStatusParam ? String(responseStatusParam).toLowerCase() : AWARD_PAYMENT.FAILED;
  const history = useHistory();

  const navigateHome = useCallback(() => {
    history.push(PATH.HOME);
  }, [history]);

  const navigateSummary = useCallback(() => {
    history.push(PATH.AWARD_PAYMENT_REQUESTS);
  }, [history]);

  return (
    <AwardPaymentLayout dataTestId='award-payment-requests-summary-page'>
      <AwardPaymentPageContainer>
        <AwardPaymentPageHeader title={pageTitle} />
        <div className={cn(styles.contentBox, className)} {...props}>
          <div className={cn(styles.iconContainer)}>
            <img
              className={cn(styles.icon)}
              src={responseStatus === AWARD_PAYMENT.SUCCEEDED ? RequestSubmittedIcon : RequestFailedIcon}
              alt={`${responseStatus === AWARD_PAYMENT.SUCCEEDED ? 'Successful Submission Icon' : 'Failed Submission Icon'}`}
            />
          </div>

          <div className={cn(styles.titleSection)}>
            <h2 className={cn(styles.title)}>{responseStatus === AWARD_PAYMENT.SUCCEEDED ? 'Request Submitted!' : 'Request Submission Failed'}</h2>
          </div>

          <div className={cn(styles.descriptionSection)}>
            <p className={cn(styles.description)}>
              {responseStatus === AWARD_PAYMENT.SUCCEEDED
                ? 'Your award payment request has been successfully submitted and is now under review.'
                : 'Your award payment request has not been successfully submitted.'}
            </p>
          </div>

          <div className={cn(styles.stepsSection)}>
            {responseStatus === AWARD_PAYMENT.SUCCEEDED && (
              <div className={cn(styles.stepsBox)}>
                <h2 className={cn(styles.stepsTitle)}>What happens next?</h2>
                <div className={cn(styles.stepsList)}>
                  {STEPS.map((step, index) => (
                    <div key={index} className={cn(styles.stepItem)}>
                      <span className={cn(styles.stepNumber)}>{index + 1}.</span>
                      <p className={cn(styles.stepText)}>{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className={cn(styles.buttonContainer)}>
            <button onClick={navigateSummary} className={cn(styles.viewRequestsButton)} style={{ letterSpacing: '0.98px' }}>
              View my requests
            </button>
            <button onClick={navigateHome} className={cn(styles.returnHomeButton)} style={{ letterSpacing: '0.98px' }}>
              Return to home
            </button>
          </div>
        </div>
      </AwardPaymentPageContainer>
    </AwardPaymentLayout>
  );
};

export default RequestResponse;
